import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Check, Crown, Zap, Star, Flame, Trophy, 
  Dumbbell, Activity, Users, Award, Loader2
} from "lucide-react";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const Subscription = () => {
  const [tiers, setTiers] = useState({});
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [processingTier, setProcessingTier] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    fetchTiers();
    fetchCurrentSubscription();
    
    // Check if returning from Stripe
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const fetchTiers = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/subscription/tiers`, { headers });
      setTiers(response.data);
    } catch (error) {
      console.error('Error fetching tiers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/subscription/my-subscription`, { headers });
      setCurrentSubscription(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please contact support.');
      setCheckingPayment(false);
      return;
    }

    setCheckingPayment(true);

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/subscription/status/${sessionId}`, { headers });
      const data = response.data;

      if (data.payment_status === 'paid') {
        toast.success('Payment successful! Welcome to your new subscription!');
        setCheckingPayment(false);
        fetchCurrentSubscription();
        // Clear the URL params
        navigate('/subscription', { replace: true });
        return;
      } else if (data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setCheckingPayment(false);
        navigate('/subscription', { replace: true });
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setCheckingPayment(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API}/subscription/billing-portal`, {
        return_url: window.location.origin
      }, { headers });
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Error opening billing portal:', error);
      toast.error('Could not open billing portal. Please try again.');
    }
  };

  const handleSubscribe = async (tierId) => {
    setProcessingTier(tierId);
    trackEvent('begin_checkout', { tier_id: tierId });
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const originUrl = window.location.origin;
      
      const response = await axios.post(`${API}/subscription/checkout`, {
        tier_id: tierId,
        origin_url: originUrl
      }, { headers });

      // Redirect to Stripe Checkout
      if (response.data.url) {
        trackEvent('checkout_redirect', { tier_id: tierId });
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      trackEvent('checkout_failed', { tier_id: tierId });
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setProcessingTier(null);
    }
  };

  const getTierIcon = (tierId) => {
    switch(tierId) {
      case 'basic': return Star;
      case 'pro': return Zap;
      case 'elite': return Crown;
      default: return Star;
    }
  };

  const getTierColor = (tierId) => {
    switch(tierId) {
      case 'basic': return { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-500', gradient: 'from-blue-600/20 to-blue-800/10' };
      case 'pro': return { bg: 'bg-purple-500/10', border: 'border-purple-500/30', text: 'text-purple-500', gradient: 'from-purple-600/20 to-purple-800/10' };
      case 'elite': return { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-500', gradient: 'from-amber-600/20 to-amber-800/10' };
      default: return { bg: 'bg-zinc-800', border: 'border-zinc-700', text: 'text-zinc-400', gradient: 'from-zinc-800 to-zinc-900' };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-3 border-orange-500/30 border-t-orange-500 rounded-full animate-spin mx-auto mb-4" />
          <div className="text-orange-500 text-lg font-heading font-bold uppercase">Loading Plans...</div>
        </div>
      </div>
    );
  }

  if (checkingPayment) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-orange-500 animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-heading font-bold text-white uppercase mb-2">Processing Payment</h2>
          <p className="text-zinc-500">Please wait while we verify your payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 p-4 md:p-6 lg:p-8">
      <motion.div 
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-5xl mx-auto space-y-8"
      >
        {/* Header */}
        <motion.div variants={itemVariants} className="text-center">
          <h1 className="text-4xl md:text-5xl font-heading font-black text-white tracking-tight uppercase">
            CHOOSE YOUR{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-amber-500">LEVEL</span>
          </h1>
          <p className="text-zinc-500 mt-2 text-sm max-w-lg mx-auto">
            Unlock your full potential with premium features designed to accelerate your transformation
          </p>
        </motion.div>

        {/* Current Subscription */}
        {currentSubscription && currentSubscription.is_active && (
          <motion.div variants={itemVariants}>
            <Card className="bg-gradient-to-r from-emerald-600/20 to-emerald-800/10 border border-emerald-500/30">
              <CardContent className="p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center">
                    <Check className="w-6 h-6 text-emerald-500" />
                  </div>
                  <div>
                    <p className="text-sm text-emerald-400">Current Plan</p>
                    <p className="text-xl font-heading font-bold text-white uppercase">{currentSubscription.tier_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Active</Badge>
                  {currentSubscription.has_stripe_subscription && (
                    <Button
                      onClick={handleManageBilling}
                      variant="outline"
                      className="bg-zinc-900 border-zinc-700 text-white hover:bg-zinc-800 text-xs font-bold uppercase"
                      data-testid="manage-billing-btn"
                    >
                      Manage Billing
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Pricing Tiers */}
        <motion.div variants={itemVariants} className="grid md:grid-cols-3 gap-4">
          {Object.entries(tiers).map(([tierId, tier], idx) => {
            const Icon = getTierIcon(tierId);
            const colors = getTierColor(tierId);
            const isCurrentTier = currentSubscription?.tier_id === tierId;
            const isPro = tierId === 'pro';

            return (
              <motion.div
                key={tierId}
                variants={itemVariants}
                whileHover={{ scale: 1.02, y: -4 }}
                className={`relative ${isPro ? 'md:-mt-4 md:mb-4' : ''}`}
              >
                {isPro && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                    <Badge className="bg-orange-600 text-white border-0 font-black text-xs">MOST POPULAR</Badge>
                  </div>
                )}
                <Card className={`bg-gradient-to-br ${colors.gradient} border ${colors.border} h-full ${isPro ? 'ring-2 ring-orange-500/50' : ''}`}>
                  <CardHeader className="pb-4">
                    <div className={`w-12 h-12 ${colors.bg} border ${colors.border} flex items-center justify-center mb-3`}>
                      <Icon className={`w-6 h-6 ${colors.text}`} />
                    </div>
                    <CardTitle className="text-2xl font-heading font-black text-white uppercase">{tier.name}</CardTitle>
                    <div className="flex items-baseline gap-1">
                      <span className={`text-4xl font-heading font-black ${colors.text}`}>${tier.price}</span>
                      <span className="text-zinc-500 text-sm">/month</span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <ul className="space-y-2">
                      {tier.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                          <Check className={`w-4 h-4 ${colors.text} mt-0.5 flex-shrink-0`} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    
                    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                      <Button
                        onClick={() => handleSubscribe(tierId)}
                        disabled={isCurrentTier || processingTier === tierId}
                        className={`w-full font-black uppercase tracking-wide h-12 ${
                          isCurrentTier 
                            ? 'bg-zinc-700 text-zinc-400 cursor-not-allowed' 
                            : isPro 
                              ? 'bg-orange-600 hover:bg-orange-500 text-white'
                              : `${colors.bg} ${colors.text} border ${colors.border} hover:bg-opacity-20`
                        }`}
                        data-testid={`subscribe-${tierId}-btn`}
                      >
                        {processingTier === tierId ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : isCurrentTier ? (
                          'Current Plan'
                        ) : (
                          `Get ${tier.name}`
                        )}
                      </Button>
                    </motion.div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Features Comparison */}
        <motion.div variants={itemVariants}>
          <Card className="bg-zinc-900/80 border border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg font-heading font-bold text-white uppercase tracking-tight">
                What's Included in Every Plan
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-4 gap-4">
                {[
                  { icon: Flame, label: "23-Week Program", desc: "Break the Cycle" },
                  { icon: Dumbbell, label: "87 Drills", desc: "Manga step-by-step guides" },
                  { icon: Activity, label: "Progress Tracking", desc: "Photos & stats" },
                  { icon: Trophy, label: "298 Badges", desc: "Earn achievements" }
                ].map((item, idx) => (
                  <div key={idx} className="text-center p-4 bg-zinc-950/50 border border-zinc-800">
                    <item.icon className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                    <p className="font-bold text-white text-sm">{item.label}</p>
                    <p className="text-xs text-zinc-500">{item.desc}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* FAQ / Trust */}
        <motion.div variants={itemVariants} className="text-center">
          <p className="text-xs text-zinc-600">
            Secure recurring payments powered by Stripe • Billed monthly • Cancel anytime
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Subscription;
